import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders,
         HttpParams } from '@angular/common/http';
import { Config } from './config';
import { User } from '../Models/user';
import { Token } from '../Models/token';
import { Credentials } from '../Models/credentials';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';


/**
 * The auth service takes care of login and logout procedures. The
 * service also provides access to some semi private methods for token
 * handling. This is there solely for the injector that manipulates
 * the http request.
 *
 * 'Normal' components should subscribe to the getUser call, and will
 * receive the logged in user whenever that happens.
 */
@Injectable()
export class AuthService {

  private subject = new BehaviorSubject<User>(null);

  // event emitter for user change. This is not the same as the
  // behavior subject, in that it only emits on change!
  private userChangedSrc = new Subject<User>();

  userChange$ = this.userChangedSrc.asObservable();


  constructor(private http: HttpClient, private config: Config) {}

  /**
   * We really need to determine if the user is there on init time.
   */
  public load(): Promise<any> {

    return this.refreshToken()
      .toPromise()
      .then((data: any) => {})
      .catch((err: any) => Promise.resolve());
  }

  /**
   * Handle the incoming credentials. This should contain a token and
   * a user. The token is stored in localstorage for later use.
   */
  private handleCredentials(data: Token): void {

    if (data && data.token) {
      localStorage.setItem(
        this.config.token_storage_key,
        JSON.stringify(data.token).replace(/\"/g, ''));
    }

    this.subject.next(this.cast(data.user));
  }

  /**
   * Log in user based on credentials and if successful, handle the credentials
   * Returns the observable User.
   */
  login(credentials: Credentials): Observable<User> {

    return this.http.post<Token>(this.config.getURL('login'),
                                 credentials)
      .map(
        data => {
          this._rmToken();
          this.handleCredentials(data)
          this.userChangedSrc.next(data.user);
          return <User>data.user;
        });
  }

  /**
   * Log in as tmp user. This is needed to be able to shop.
   */
  loginTmp(): Observable<User> {

    return this.http.post<Token>(
      this.config.getURL('login_tmp'), {})
      .map(
        data => {
          this._rmToken();
          this.handleCredentials(data)
          this.userChangedSrc.next(data.user);
          return <User>data.user;
        });
  }

  /**
   * Log out user. Remove token if successful.
   */
  logout(user: User): void {

    this.http.post(user._links.logout, {}).subscribe(
      data => {
        this._rmToken();
        this.subject.next(null);
        this.userChangedSrc.next(null);
      }
    );
  }

  requestpwd(email: string): Observable<any> {

    return this.http.post(this.config.getURL('reset_pwd'), {'email': email});
  }

  resetPwd(data: any): Observable<any> {
    return this.http.post(this.config.getURL('reset_pwd'), data);
  }

  /**
   * Get user as Observable. This is the main method for this service
   * that will provide the user whenever available.
   */
  public getUser(): Observable<User> {

    return this.subject.asObservable();
  }

  _rmToken(): void {

    localStorage.removeItem(this.config.token_storage_key);
  }

  _getToken(): string {

    return localStorage.getItem(this.config.token_storage_key);
  }

  public refreshToken(): Observable<string> {

    var body = {'token': this._getToken()}

    // return empty string (??) if no token has been set
    if (!body['token']) {
      return Observable.of("");
    }

    return this.http.post<Token>(
      this.config.base + '/auth/refresh-token/', body)
      .map(
        data => {
          this.handleCredentials(data);
          return <string>data.token;
        })
      .catch((err, caught) => {
        this._rmToken();

        return "";
      });
  }

  private cast(data): User {

    let user = new User();

    user.setData(data);

    return user;
  }

}



// WEBPACK FOOTER //
// ./src/app/Services/auth.ts